import { useCallback, useEffect, useState } from "react";
import { ConfigProposal, ConfigProposalResponse, api, isError } from "../api";

/** Shown at the top of Trading-config when the live config no longer
 *  matches the enabled strategies - e.g. after starring a different
 *  favourite or switching timeframe. Each row is a concrete diff with the
 *  reason it is proposed; applying is explicit (one row or all of them),
 *  because these values change live trading behaviour. */
export function ConfigProposalCard({ onApplied }: { onApplied: () => Promise<void> }) {
  const [data, setData] = useState<ConfigProposalResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const r = await api.configProposal();
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setError(null);
    setData(r);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function apply(values: Record<string, number | string | string[]>) {
    setBusy(true);
    setError(null);
    const r = await api.setTradingConfigBulk(values);
    setBusy(false);
    if (isError(r)) {
      setError(r.error);
      return;
    }
    await onApplied();
    await refresh();
  }

  if (error) {
    return (
      <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
        Couldn’t check config against your strategies: {error}
      </p>
    );
  }
  if (data === null) return null;

  const { proposals, conflicts, unvalidated, enabled } = data;
  // Fingerprint of the current suggestion set, so "dismiss" hides THIS
  // set but a later, different one still surfaces.
  const signature = proposals.map((p) => `${p.key}=${String(p.proposed)}`).join("|");
  const hidden = dismissed === signature;

  if (proposals.length === 0 && conflicts.length === 0 && unvalidated.length === 0) {
    return enabled.length > 0 ? (
      <p className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success">
        Config matches your {enabled.length} enabled{" "}
        {enabled.length === 1 ? "strategy" : "strategies"}.
      </p>
    ) : null;
  }

  return (
    <section className="rounded-xl border border-warn/40 bg-warn/5 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-fg">
            {proposals.length > 0
              ? `${proposals.length} setting${proposals.length === 1 ? "" : "s"} don’t match your strategies`
              : "Strategy check"}
          </h2>
          <p className="mt-0.5 text-xs text-fg-muted">
            Derived from {enabled.join(", ") || "no enabled strategies"} — each
            strategy’s validated run knows the timeframe and bracket it was
            tested with.
          </p>
        </div>
        {proposals.length > 0 && !hidden && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                void apply(Object.fromEntries(proposals.map((p) => [p.key, p.proposed])))
              }
              disabled={busy}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-primary/90 disabled:opacity-50"
            >
              {busy ? "applying…" : `apply all (${proposals.length})`}
            </button>
            <button
              type="button"
              onClick={() => setDismissed(signature)}
              className="rounded-md border border-border px-2 py-1.5 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
            >
              dismiss
            </button>
          </div>
        )}
      </div>

      {proposals.length > 0 && !hidden && (
        <table className="mt-3 w-full text-xs">
          <thead className="text-fg-muted">
            <tr>
              <th className="p-1 text-left">setting</th>
              <th className="p-1 text-right">current</th>
              <th className="p-1 text-right">proposed</th>
              <th className="p-1 text-left">why</th>
              <th className="p-1" />
            </tr>
          </thead>
          <tbody>
            {proposals.map((p) => (
              <tr key={p.key} className="border-t border-border/60 align-top">
                <td className="p-1">
                  <div className="text-fg">{p.label}</div>
                  <div className="font-mono text-[10px] text-fg-muted">{p.key}</div>
                </td>
                <td className="p-1 text-right font-mono text-fg-muted">
                  {fmt(p.current)} {p.unit}
                </td>
                <td className="p-1 text-right font-mono font-bold text-success">
                  {fmt(p.proposed)} {p.unit}
                </td>
                <td className="p-1 text-fg-muted">{p.reason}</td>
                <td className="p-1 text-right">
                  <button
                    type="button"
                    onClick={() => void apply({ [p.key]: p.proposed })}
                    disabled={busy}
                    className="rounded border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
                  >
                    apply
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {hidden && proposals.length > 0 && (
        <p className="mt-2 text-xs text-fg-muted">
          Dismissed.{" "}
          <button
            type="button"
            onClick={() => setDismissed(null)}
            className="underline hover:text-fg"
          >
            show again
          </button>
        </p>
      )}

      {conflicts.map((c) => (
        <p key={c.key} className="mt-2 text-xs text-warn">
          <strong>{c.label}:</strong> your strategies were tested with different
          values (
          {c.values.map((v) => `${v.strategy} ${v.value}`).join(", ")}) — using{" "}
          <span className="font-mono">{c.using}</span>’s. One bracket applies to
          the converged entry, so pick deliberately.
        </p>
      ))}

      {unvalidated.length > 0 && (
        <p className="mt-2 text-xs text-fg-muted">
          No validated run behind: <span className="font-mono">{unvalidated.join(", ")}</span>.
          Back-test and ★ save them so their settings can be derived.
        </p>
      )}
    </section>
  );
}

function fmt(v: ConfigProposal["current"]): string {
  if (Array.isArray(v)) return v.join(",");
  return String(v);
}
